import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import useLangNavigate from "../../hooks/useLangNavigate";
import { setUser } from "../../redux/slices/userSlice";
import Button from "../../components/buttons/Button";
import Alert from "../../components/utils/Alert";
import OffoodLogo from "../../components/brand/OffoodLogo";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-light-svg-icons";
import { faApple } from "@fortawesome/free-brands-svg-icons";
import { useTranslation } from "react-i18next";
import useApi from "../../hooks/useApi";
import { useAuth } from "../../hooks/useAuth";
import {
  postAttemptLogin,
  postGoogleLogin,
  postAppleLogin,
} from "../../services/authService";
import { withAuthenticatedRoute } from "../../components/HOC/RoutingLogic";
import { usePageMeta } from "../../hooks/useMeta";
import OTPInput from "react-otp-input";
import { GoogleLogin } from "@react-oauth/google";
import { getEnvVariable } from "../../utils/config";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [twoFACode, setTwoFACode] = useState(null);
  const { t, i18n } = useTranslation();
  const navigate = useLangNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();

  const {
    error: attemptLoginError,
    status: attemptLoginStatus,
    request: attemptLoginRequest,
    loading: loginLoading,
  } = useApi(postAttemptLogin);

  const { error: googleLoginError, request: googleLoginRequest } =
    useApi(postGoogleLogin);
  const { error: appleLoginError, request: appleLoginRequest } =
    useApi(postAppleLogin);

  const AppleLoginButton = () => {
    useEffect(() => {
      if (window.AppleID) {
        window.AppleID.auth.init({
          clientId: getEnvVariable("REACT_APP_APPLE_CLIENT_ID"),
          scope: "name email",
          redirectURI: window.location.origin,
          state: "state",
          usePopup: true,
        });
      }
    }, []);

    const handleAppleLogin = () => {
      window.AppleID.auth
        .signIn()
        .then((res) => {
          const tokenId = res.authorization.id_token;
          const firstname = res.user.name.firstName;
          const lastname = res.user.name.lastName;
          const response = appleLoginRequest(tokenId, firstname, lastname);
          if (response) {
            logUser(response.data);
            navigate(`dashboard`);
          } else console.error("Apple auth failed:", appleLoginError);
        })
        .catch((error) => {
          console.error("Apple login error:", error);
        });
    };
    return (
      <button onClick={handleAppleLogin} className="apple-login-button">
        <FontAwesomeIcon icon={faApple} />
        <span className="w-100 text-center">{t("login.form.appleLogin")}</span>
      </button>
    );
  };

  const goToHome = () => {
    navigate("/");
  };

  useEffect(() => {
    if (isAuthenticated) navigate(`dashboard`);
  }, [isAuthenticated, navigate]);

  const attemptLogin = async () => {
    await attemptLoginRequest({ email, password, twoFACode })
      .then((response) => {
        logUser(response.data);
        navigate(`dashboard`);
      })
      .catch((error) => {
        console.error("Error during login:", error);
      });
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const idToken = credentialResponse.credential;
      const response = await googleLoginRequest(idToken);
      if (response) {
        logUser(response.data);
        navigate(`dashboard`);
      } else console.error("Google auth failed:", googleLoginError);
    } catch (error) {
      toast.error(error.message);
      console.error("Google auth error:", error);
    }
  };

  const logUser = (user) => {
    dispatch(setUser(user));
    localStorage.setItem("token", user.token);
  };

  return (
    <>
      {usePageMeta("login")}
      <div id="login">
        <div className="login-illustration" />
        <div className="login-content">
          <div className="flex justify-between">
            <Button variant="tertiary" onClick={goToHome}>
              <FontAwesomeIcon size="xl" icon={faArrowLeftLong} />
            </Button>
            <OffoodLogo variant="red" />
          </div>
          <h1 className="mb-0">{t("login.title")}</h1>
          <p className="p1 flex gap-1 m-0">
            <span>{t("login.noAccount")}</span>
            <Button
              className="true-link"
              link
              onClick={() => navigate("register")}
            >
              <span className="p1">{t("login.register")}</span>
            </Button>
          </p>
          {attemptLoginError && attemptLoginStatus !== 428 && (
            <Alert variant="danger">{attemptLoginError}</Alert>
          )}
          <div className="p1 login-form">
            <div>
              <label htmlFor="email">{t("login.form.email.label")}</label>
              <input
                id="email"
                type="text"
                className="border-1"
                placeholder={t("login.form.email.label")}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password">{t("login.form.password.label")}</label>
              <input
                id="password"
                className="border-1"
                type="password"
                placeholder={t("login.form.password.label")}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            {(attemptLoginStatus === 428 || attemptLoginStatus === 403) && (
              <div>
                <label htmlFor="twoFACode">
                  {t("login.form.twoFACode.label")}
                </label>
                <OTPInput
                  value={twoFACode}
                  numInputs={6}
                  separator={<span></span>}
                  onChange={setTwoFACode}
                  renderInput={(props) => <input {...props} />}
                  inputStyle={"otp-input"}
                  containerStyle={"otp-container"}
                />
              </div>
            )}
            <div className="login-psw-and-support flex justify-between align-end">
              <Button
                className="true-link"
                link
                onClick={() => navigate("forgotPassword")}
              >
                <span className="p1">{t("login.form.forgotPassword")}</span>
              </Button>
              <Button
                className="true-link"
                link
                onClick={() => navigate("support")}
              >
                <span>{t("utils.common.support")}</span>
              </Button>
            </div>
            <Button
              size="md"
              onClick={attemptLogin}
              onEnter={attemptLogin}
              loading={loginLoading}
            >
              <span className="p1">{t("login.form.next")}</span>
            </Button>
            <div className="flex column gap-2 mt-4">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                useOneTap
                onError={(error) => console.error("Google login error:", error)}
                locale={i18n.language}
              />
              <div style={{ display: "none" }}>
                <AppleLoginButton />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default withAuthenticatedRoute(Login);
